import { Injectable } from '@angular/core';

import { User } from '@mitel/cloudlink-sdk';
import { UserLicense } from '@mitel/cloudlink-sdk/billing';
import { AppInjector } from './../../app-injector';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { OrdersService } from './orders.service';
import { ProductLicense, ProductLicenseItemServiceInf } from '../interfaces/product-licenses-interfaces';
import { productCodes, unifyPhoneCodes } from '../constants/constants';

const logContext = 'UserProdUnifyPhoneService';

@Injectable({
  providedIn: 'root'
})
export class UserProdUnifyPhoneService implements ProductLicenseItemServiceInf {
  private adminService: AdminService;
  private accountService: AccountsService;
  private ordersService: OrdersService;

  constructor() { }

  initializeServices() {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.accountService)
      this.accountService = AppInjector.injector.get(AccountsService);

    if (!this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);
  }

  async canBeAdded(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    return Promise.resolve(false);
  }

  async hasRequiredAccountIntegrations(user: User, accountIntegrations: string[]): Promise<boolean> {
    this.initializeServices();

    const isIntegrationAdded = accountIntegrations?.includes(productCodes.UNIFY_PHONE);

    if (isIntegrationAdded) {
      try {
        const userLicense = await this.isLicenseEnabledForUser(user);
        return !!userLicense;
      } catch (error) {
        console.error(`${logContext}.hasRequiredAccountIntegrations - Error determining the account tags: `, error);
        return false;
      };
    }
    return false;
  }

  getConfigurationDialog(setupComplete?: boolean, user?: User): Promise<any> {
    return Promise.resolve(undefined);
  }

  async isSetupComplete(user: User): Promise<boolean> {
    this.initializeServices();

    const userLicense = await this.isLicenseEnabledForUser(user);
    return !!userLicense;
  }

  async handleProductStateChange(user: User, state: boolean): Promise<any> {
    console.warn(`${logContext}.handleProductStateChange - Trying to assign Unify Phone license in the UI`);
    return Promise.resolve(undefined);
  }

  isButtonDisabled(user: User, product: ProductLicense, isSubComponent?: boolean): Promise<boolean> {
    return Promise.resolve(true);
  }

  async isProductEnabled(user: User, isSubComponent?: boolean): Promise<boolean> {
    this.initializeServices();

    return await this.isSetupComplete(user);
  }

  async getDisplayName?(user: User): Promise<string> {    
   const userLicense = await this.isLicenseEnabledForUser(user);
      return userLicense ?
        'product_licenses.unifyPhone.name' :
        'product_licenses.unifyPhone.no-license-assigned';
  }

  async isLicenseEnabledForUser(user: User): Promise<UserLicense | undefined> {
    this.initializeServices();

    try {
      const userLicenses = await this.ordersService.getUserLicenses(user.userId);

      return userLicenses.find(license => {
        return license.enable && unifyPhoneCodes.USER_PRODUCT === license.name;
      });
    } catch (error) {
      console.error(`${logContext}.isLicenseEnabledForUser - Failed to get user licenses for Unify Phone: `, error);
    }
  }
}
